import { useEffect, useState, type JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import { MANUAL_FIELDS } from './manual-fields.js'
import type { ChannelProviderName, ConnectedAccount } from '../../../pages/admin/channels-types.js'

export interface EditAccountModalProps {
  visible: boolean
  provider: ChannelProviderName
  account: ConnectedAccount | null
  onCancel: () => void
  onSaved: () => Promise<void>
}

/**
 * Edit a connected account. Every account can be renamed; manual accounts
 * (auth_type === 'manual') can additionally update their credential fields —
 * OAuth accounts have no user-editable credentials, since tokens come from
 * the provider's own OAuth flow.
 */
export default function EditAccountModal(props: EditAccountModalProps): JSX.Element {
  const toast = useToast()
  const [displayName, setDisplayName] = useState('')
  const [fields, setFields] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const isManual = props.account?.authType === 'manual'
  const manualFields = MANUAL_FIELDS[props.provider] ?? [{ key: 'apiKey', label: 'API Key', type: 'password' as const }]

  useEffect(() => {
    setDisplayName(props.account?.displayName ?? '')
    setFields({})
  }, [props.account?.accountKey, props.visible])

  function handleClose(): void {
    props.onCancel()
  }

  async function handleSubmit(): Promise<void> {
    if (!props.account) return
    if (!displayName.trim()) {
      toast.error('Account name is required.')
      return
    }
    setSubmitting(true)
    try {
      const filledFields = Object.fromEntries(Object.entries(fields).filter(([, v]) => v.trim().length > 0))
      await api.put(`/admin/channels/${props.provider}/${encodeURIComponent(props.account.accountKey)}`, {
        displayName: displayName.trim(),
        ...(isManual && Object.keys(filledFields).length > 0 ? { fields: filledFields } : {}),
      })
      await props.onSaved()
      toast.success('Account updated')
      handleClose()
    } catch (e) {
      toast.error('Failed to update account', e instanceof Error ? e.message : 'Failed to update account.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      visible={props.visible}
      header={props.account ? `Edit ${props.account.displayName}` : 'Edit Account'}
      modal
      style={{ width: '30rem' }}
      onHide={handleClose}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Account Name</label>
          <InputText
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full"
            disabled={submitting}
          />
        </div>

        {isManual ? (
          <>
            <p className="text-xs text-gray-500">
              Leave a field blank to keep its current value unchanged.
            </p>
            {manualFields.map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                <InputText
                  type={f.type === 'password' ? 'password' : 'text'}
                  value={fields[f.key] ?? ''}
                  onChange={(e) => setFields((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  className="w-full"
                  disabled={submitting}
                  placeholder="Unchanged"
                />
              </div>
            ))}
          </>
        ) : (
          <p className="text-xs text-gray-500">
            This account's credentials are managed automatically via OAuth — only the account name can
            be changed here.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button label="Cancel" onClick={handleClose} disabled={submitting} severity="secondary" />
          <Button label="Save" onClick={() => void handleSubmit()} disabled={submitting} loading={submitting} />
        </div>
      </div>
    </Dialog>
  )
}
